/**
 * Integrytas Group - Common Security Module (CSM)
 * Role-set reconciliation for the new-user provision form.
 *
 * When the requester changes the Job Description, the form offers logical ways
 * to combine the roles they already picked (current, S) with the old job's
 * default roles (oldDefaults, O) and the new job's default roles
 * (newDefaults, N). All operations are pure set math over role ids.
 */
export type RoleReconcileStrategy = 'swap' | 'replace' | 'add' | 'remove_old' | 'keep';

/** Deduped, ascending-sorted copy — the canonical shape all strategies return. */
function normalize(ids: number[]): number[] {
  return [...new Set(ids)].sort((a, b) => a - b);
}

export function applyRoleStrategy(
  strategy: RoleReconcileStrategy,
  current: number[],
  oldDefaults: number[],
  newDefaults: number[],
): number[] {
  const S = current;
  const O = new Set(oldDefaults);
  const N = newDefaults;
  switch (strategy) {
    case 'swap':       return normalize([...S.filter(id => !O.has(id)), ...N]);
    case 'replace':    return normalize(N);
    case 'add':        return normalize([...S, ...N]);
    case 'remove_old': return normalize(S.filter(id => !O.has(id)));
    case 'keep':       return normalize(S);
  }
}

export function reconcilePreview(
  result: number[],
  current: number[],
): { total: number; kept: number; added: number } {
  const cur = new Set(current);
  const kept  = result.filter(id => cur.has(id)).length;
  const added = result.filter(id => !cur.has(id)).length;
  return { total: result.length, kept, added };
}
